import axios from 'axios';
import { v4 as createUuid } from 'uuid';
import { globalConfig } from '../configuration/config';

export const MODEL_FULL_KTX2_ACTION_TYPE = 4;
export const MODEL_FULL_KTX2_ACTION_MESSAGE_CONTENT = 'Статус KTX2-обработки GLB';
export const MODEL_FULL_KTX2_POLL_INTERVAL_MS = 3000;

const MODEL_FULL_KTX2_STATUSES = [
  'queued',
  'running',
  'waiting_for_replace',
  'succeeded',
  'failed',
  'superseded',
];

export class ModelFullKtx2PostprocessError extends Error {
  constructor(messageKey, details = {}) {
    super(messageKey);
    this.name = 'ModelFullKtx2PostprocessError';
    this.messageKey = messageKey;
    this.details = details;
  }
}

function trimTrailingSlash(url) {
  return url.replace(/\/+$/, '');
}

function getApiBaseUrl() {
  return trimTrailingSlash(globalConfig.config.common.api);
}

function normalizeText(value) {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalizedValue = value.trim();
  return normalizedValue.length > 0 ? normalizedValue : undefined;
}

function isAbortError(error) {
  return error?.name === 'AbortError' || error?.code === 'ERR_CANCELED';
}

function normalizeStatus(value) {
  const normalizedValue = normalizeText(value);

  if (normalizedValue === undefined) {
    return undefined;
  }

  return normalizedValue
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase();
}

function unwrapApiPayload(responseData) {
  let payload = responseData;

  for (let depth = 0; depth < 3; depth += 1) {
    if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
      return payload;
    }

    if (payload.tVal !== undefined || payload.TVal !== undefined) {
      payload = payload.tVal ?? payload.TVal;
      continue;
    }

    if (payload.data !== undefined || payload.Data !== undefined) {
      payload = payload.data ?? payload.Data;
      continue;
    }

    return payload;
  }

  return payload;
}

function createNoSubmissionStatus() {
  return {
    attempt: null,
    errorCode: null,
    errorMessage: null,
    phase: null,
    status: 'none',
    submissionId: null,
    updatedAt: null,
  };
}

function normalizeAttempt(value) {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) {
    return value;
  }

  return null;
}

export function normalizeModelFullKtx2PostprocessResponse(
  responseData,
  { fallbackStatus } = {},
) {
  const payload = unwrapApiPayload(responseData);

  if (
    payload === null ||
    payload === undefined ||
    payload?.found === false ||
    payload?.Found === false ||
    payload?.hasSubmission === false ||
    payload?.HasSubmission === false ||
    payload?.submission === null ||
    payload?.Submission === null ||
    payload?.postprocess === null ||
    payload?.Postprocess === null
  ) {
    return createNoSubmissionStatus();
  }

  const submission =
    payload.submission ??
    payload.Submission ??
    payload.postprocess ??
    payload.Postprocess ??
    payload;
  const status = normalizeStatus(submission?.status ?? submission?.Status) ?? fallbackStatus;

  if (status === 'none') {
    return createNoSubmissionStatus();
  }

  if (!MODEL_FULL_KTX2_STATUSES.includes(status)) {
    throw new ModelFullKtx2PostprocessError(
      'modelFullKtx2Postprocess.errors.invalidResponse',
    );
  }

  const submissionId = normalizeText(
    submission?.submissionId ??
    submission?.SubmissionId ??
    submission?.id ??
    submission?.Id,
  );

  if (submissionId === undefined) {
    throw new ModelFullKtx2PostprocessError(
      'modelFullKtx2Postprocess.errors.invalidResponse',
    );
  }

  return {
    attempt: normalizeAttempt(submission?.attempt ?? submission?.Attempt),
    errorCode: normalizeText(submission?.errorCode ?? submission?.ErrorCode) ?? null,
    errorMessage: normalizeText(submission?.errorMessage ?? submission?.ErrorMessage) ?? null,
    phase: normalizeText(submission?.phase ?? submission?.Phase) ?? null,
    status,
    submissionId,
    updatedAt: normalizeText(submission?.updatedAt ?? submission?.UpdatedAt) ?? null,
  };
}

export function canRequestModelFullKtx2Postprocess(status) {
  return status?.status === 'none' || status?.status === 'failed';
}

export function createModelFullKtx2ClientRequestId() {
  return createUuid();
}

export function buildModelFullKtx2ActionMessagePayload({
  calculationGuid,
  projectGuid,
}) {
  const normalizedCalculationGuid = normalizeText(calculationGuid);
  const normalizedProjectGuid = normalizeText(projectGuid);

  if (normalizedCalculationGuid === undefined || normalizedProjectGuid === undefined) {
    throw new ModelFullKtx2PostprocessError(
      'modelFullKtx2Postprocess.errors.invalidRequest',
    );
  }

  return {
    ActionType: MODEL_FULL_KTX2_ACTION_TYPE,
    Content: MODEL_FULL_KTX2_ACTION_MESSAGE_CONTENT,
    FormatType: 'Action',
    GuidCalculation: normalizedCalculationGuid,
    GuidProject: normalizedProjectGuid,
    Type: 'System',
  };
}

export async function sendModelFullKtx2ActionMessage({
  calculationGuid,
  projectGuid,
  signal,
}) {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/ChatMessages/SendMessage`,
      buildModelFullKtx2ActionMessagePayload({
        calculationGuid,
        projectGuid,
      }),
      {
        signal,
        withCredentials: true,
      },
    );

    return response.data;
  } catch (error) {
    if (isAbortError(error) || error instanceof ModelFullKtx2PostprocessError) {
      throw error;
    }

    throw new ModelFullKtx2PostprocessError(
      'modelFullKtx2Postprocess.errors.actionPersistFailed',
      {
        responseStatusCode: error?.response?.status,
      },
    );
  }
}

export async function getModelFullKtx2Postprocess({
  calculationGuid,
  signal,
}) {
  const normalizedCalculationGuid = normalizeText(calculationGuid);

  if (normalizedCalculationGuid === undefined) {
    throw new ModelFullKtx2PostprocessError(
      'modelFullKtx2Postprocess.errors.invalidRequest',
    );
  }

  try {
    const response = await axios.get(
      `${getApiBaseUrl()}/Calculation/ModelFullKtx2Postprocess`,
      {
        params: {
          calculationGuid: normalizedCalculationGuid,
        },
        signal,
        withCredentials: true,
      },
    );

    return normalizeModelFullKtx2PostprocessResponse(response.data);
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    if (error?.response?.status === 404) {
      return createNoSubmissionStatus();
    }

    if (error instanceof ModelFullKtx2PostprocessError) {
      throw error;
    }

    throw new ModelFullKtx2PostprocessError(
      'modelFullKtx2Postprocess.errors.statusFailed',
      {
        responseStatusCode: error?.response?.status,
      },
    );
  }
}

export async function requestModelFullKtx2Postprocess({
  calculationGuid,
  clientRequestId,
  signal,
  submissionId,
}) {
  const normalizedCalculationGuid = normalizeText(calculationGuid);
  const normalizedClientRequestId = normalizeText(clientRequestId);
  const normalizedSubmissionId = normalizeText(submissionId);

  if (normalizedCalculationGuid === undefined || normalizedClientRequestId === undefined) {
    throw new ModelFullKtx2PostprocessError(
      'modelFullKtx2Postprocess.errors.invalidRequest',
    );
  }

  const requestBody = {
    calculationGuid: normalizedCalculationGuid,
    clientRequestId: normalizedClientRequestId,
  };

  if (normalizedSubmissionId !== undefined) {
    requestBody.submissionId = normalizedSubmissionId;
  }

  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/Calculation/RequestModelFullKtx2Postprocess`,
      requestBody,
      {
        signal,
        withCredentials: true,
      },
    );

    return normalizeModelFullKtx2PostprocessResponse(response.data, {
      fallbackStatus: 'queued',
    });
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    if (error instanceof ModelFullKtx2PostprocessError) {
      throw error;
    }

    const responseStatusCode = error?.response?.status;
    const messageKey = responseStatusCode === 503
      ? 'modelFullKtx2Postprocess.errors.unknownOutcome'
      : 'modelFullKtx2Postprocess.errors.requestFailed';

    throw new ModelFullKtx2PostprocessError(messageKey, {
      responseStatusCode,
    });
  }
}

export function isModelFullKtx2UnknownOutcomeError(error) {
  return (
    error instanceof ModelFullKtx2PostprocessError &&
    error.details.responseStatusCode === 503
  );
}
